// T-180 (v1.8.57, Session 44, 05.10.2026): "Erstattung erfassen" an einer bezahlten Rechnung (Vollerstattung, Stornorechnung, Mail an den Gast).
// Reine Hilfsfunktionen für die Oberfläche; die verbindliche Prüfung macht der Server (Business.Invoicing.Erstattung). Getestet in erstattung.test.ts.

import { statusName } from './rechnungskorrektur';

export type ErstattungWeg = 'Stripe' | 'PayPal' | 'Ueberweisung' | 'Bar';

export const ERSTATTUNG_WEGE: { wert: ErstattungWeg; text: string }[] = [
  { wert: 'Stripe', text: 'Karte (Stripe)' },
  { wert: 'PayPal', text: 'PayPal' },
  { wert: 'Ueberweisung', text: 'Überweisung' },
  { wert: 'Bar', text: 'bar' },
];

export const ERSTATTUNG_HINWEIS =
  'Erst die Erstattung beim Zahlungsanbieter bzw. per Überweisung oder bar ausführen, danach hier erfassen. Das System storniert die Rechnung, ' +
  'setzt den Zahlungseingang auf 0, erstellt die Stornorechnung (ST-..., im Rechnungsarchiv) und sendet sie auf Wunsch per E-Mail an den Gast. ' +
  'In dieser Version nur über den vollen Zahlungseingang.';

export const MIN_GRUND = 3;
export const MAX_REFERENZ = 100;

interface RechnungKurz {
  type?: string | null;
  typeDisplayName?: string | null;
  status?: string | number | null;
  amountPaid?: number | null;
}

/** Ob der Bereich "Erstattung erfassen" erscheint: Zahlungseingang größer 0, nicht storniert, kein Angebot/Storno/Gutschrift. */
export function istErstattbar(r: RechnungKurz | null | undefined): boolean {
  if (!r) return false;
  const typen = [r.type, r.typeDisplayName].map(t => (t ?? '').toString().toLowerCase());
  if (typen.some(t => t === 'angebot' || t === 'storno' || t === 'gutschrift')) return false;
  if (statusName(r.status ?? '') === 'Cancelled') return false;
  return (r.amountPaid ?? 0) > 0;
}

/** Vorbelegung des Wegs aus den Verbuchungsnotizen der Rechnung (Online-Zahlung Stripe/PayPal, Zahlung erfasst, Bankabgleich); sonst leer. */
export function vorgabeWeg(adminNotes: string | null | undefined): ErstattungWeg | '' {
  const n = (adminNotes ?? '').toLowerCase();
  if (/online-zahlung stripe|\bstripe\b/.test(n)) return 'Stripe';
  if (/paypal/.test(n)) return 'PayPal';
  if (/zahlung erfasst \(bar\)/.test(n)) return 'Bar';
  if (/bankabgleich|überweisung|ueberweisung/.test(n)) return 'Ueberweisung';
  return '';
}

/** Prüft die Eingaben (Datum und heute als yyyy-mm-dd). Meldung oder null. */
export function pruefeErstattung(datum: string, heute: string, weg: string, referenz: string, grund: string): string | null {
  if (!datum || datum.slice(0, 10) > heute.slice(0, 10)) return 'Bitte das Datum der Erstattung angeben (nicht in der Zukunft).';
  if (!ERSTATTUNG_WEGE.some(w => w.wert === weg)) return 'Bitte den Weg der Erstattung wählen (Karte, PayPal, Überweisung, bar).';
  if ((referenz ?? '').trim().length > MAX_REFERENZ) return `Die Referenz darf höchstens ${MAX_REFERENZ} Zeichen haben.`;
  if ((grund ?? '').replace(/\s+/g, ' ').trim().length < MIN_GRUND) return `Bitte einen Grund angeben (mindestens ${MIN_GRUND} Zeichen).`;
  return null;
}

/** Betrag deutsch mit Euro: 11,8 -> "11,80 €". */
export const euroText = (betrag: number): string =>
  `${(Math.round((betrag ?? 0) * 100) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
